import type { ClientTool } from '../hooks/useClientTools';
import {
  ListAllAgentsDocument,
  GetAgentDocument,
  CreateAgentDocument,
  UpdateAgentDocument,
  GetKnowledgeBasesDocument,
  AttachKnowledgeBaseToAgentDocument,
  DetachKnowledgeBaseFromAgentDocument,
  getNodes,
} from '@matrix-forge/graphql-client';

/**
 * Minimal GraphQL client interface matching the Apollo Client subset we need.
 * Avoids a direct dependency on `@apollo/client` in the `ui` package.
 */
export interface GraphQLClient {
  query(options: { query: any; variables?: any; fetchPolicy?: string }): Promise<{ data: any }>;
  mutate(options: {
    mutation: any;
    variables?: any;
    refetchQueries?: any[];
  }): Promise<{ data: any }>;
}

/**
 * All persona tool names (superset). Used by CLIENT_TOOL_NAMES / isClientTool()
 * to identify client-side tools. This is intentionally static — even when
 * includeKbTools is false, having the names here is harmless because the LLM
 * never receives tool definitions for excluded tools.
 */
export const PERSONA_TOOL_NAMES = [
  'list_personas',
  'get_persona',
  'create_persona',
  'update_persona',
  'list_knowledge_bases',
  'attach_knowledge_base',
  'detach_knowledge_base',
] as const;

/**
 * Normalize a tools config object to ensure all required top-level keys exist
 * and fill in default consent_mode values where missing. This prevents the
 * backend from ignoring the tools config due to missing structure.
 */
function normalizeToolsConfig(tools: Record<string, any>): Record<string, any> {
  const normalized: Record<string, any> = {
    predefined_names: tools.predefined_names ?? [],
    allow_patterns: tools.allow_patterns ?? [],
    deny_patterns: tools.deny_patterns ?? [],
    system_tools: tools.system_tools ?? {},
    mcp_servers: {},
    workflow_tools: tools.workflow_tools ?? {},
    connectors: tools.connectors ?? {},
    collaborator_agents: tools.collaborator_agents ?? {},
  };

  // Normalize MCP servers: ensure each tool_config entry has consent_mode
  const mcpServers = tools.mcp_servers ?? {};
  for (const [slug, serverConfig] of Object.entries(mcpServers)) {
    const server = serverConfig as Record<string, any>;
    const toolConfigs: Record<string, any> = {};

    if (server.tool_configs && typeof server.tool_configs === 'object') {
      for (const [toolName, toolConfig] of Object.entries(server.tool_configs)) {
        const tc = toolConfig as Record<string, any>;
        toolConfigs[toolName] = {
          enabled: tc.enabled ?? true,
          consent_mode: tc.consent_mode ?? 'auto',
        };
      }
    }

    normalized.mcp_servers[slug] = {
      enabled: server.enabled ?? true,
      consent_mode: server.consent_mode ?? 'manual',
      tool_configs: toolConfigs,
    };
  }

  // Normalize system_tools: ensure each entry has consent_mode
  const systemTools = normalized.system_tools;
  for (const [toolName, toolConfig] of Object.entries(systemTools)) {
    const tc = toolConfig as Record<string, any>;
    systemTools[toolName] = {
      enabled: tc.enabled ?? true,
      consent_mode: tc.consent_mode ?? 'auto',
    };
  }

  return normalized;
}

/**
 * Decodes a Relay global ID (base64 of "Type:uuid") to the raw UUID.
 * Returns the original string if it's not a valid Relay ID.
 */
function decodeRelayId(id: string): string {
  try {
    const decoded = atob(id);
    const colonIndex = decoded.indexOf(':');
    if (colonIndex > 0) {
      return decoded.substring(colonIndex + 1);
    }
  } catch {
    // Not valid base64, return as-is
  }
  return id;
}

/**
 * Converts an array of collaborator agent entries into the collaborator_agents
 * config map expected by the backend: { [agentId]: { enabled, name, description } }
 *
 * Agent IDs are decoded from Relay global IDs to raw UUIDs since the backend
 * stores collaborator_agents keyed by raw UUID.
 */
function buildCollaboratorAgentsConfig(
  agents: Array<{ id: string; name: string; description?: string }>
): Record<string, { enabled: boolean; name: string; description?: string }> {
  const config: Record<string, { enabled: boolean; name: string; description?: string }> = {};
  for (const agent of agents) {
    const rawId = decodeRelayId(agent.id);
    config[rawId] = {
      enabled: true,
      name: agent.name,
      ...(agent.description ? { description: agent.description } : {}),
    };
  }
  return config;
}

export interface PersonaToolsOptions {
  /** Include KB tools (list, attach, detach). Defaults to true. */
  includeKbTools?: boolean;
}

export function createPersonaTools(
  client: GraphQLClient,
  options?: PersonaToolsOptions
): ClientTool[] {
  const tools: ClientTool[] = [
    createListPersonasTool(client),
    createGetPersonaTool(client),
    createCreatePersonaTool(client),
    createUpdatePersonaTool(client),
  ];

  if (options?.includeKbTools !== false) {
    tools.push(
      createListKnowledgeBasesTool(client),
      createAttachKnowledgeBaseTool(client),
      createDetachKnowledgeBaseTool(client)
    );
  }

  return tools;
}

function createListPersonasTool(
  client: GraphQLClient
): ClientTool {
  return {
    name: 'list_personas',
    description: `List all available personas (agents) in the current tenant.
Returns both template personas and custom personas with their id, name,
description, model, system message, greeting, enabled status, and
attached knowledge bases. Use this to discover what personas already exist
before creating or updating one.`,
    parameters: {
      type: 'object',
      properties: {},
    },
    handler: async () => {
      try {
        const { data } = await client.query({
          query: ListAllAgentsDocument,
          fetchPolicy: 'network-only',
        });

        const templateAgents = getNodes(data?.templateAgents?.edges) ?? [];
        const customAgents = getNodes(data?.customAgents?.edges) ?? [];
        const allAgents = [...templateAgents, ...customAgents];

        const personas = allAgents.map(agent => ({
          id: agent.id,
          name: agent.name,
          description: agent.description ?? null,
          isTemplate: agent.isTemplate ?? false,
          isPublic: agent.isPublic ?? false,
          isEnabled: agent.isEnabled ?? true,
          systemMessage: agent.systemMessage ?? null,
          agentGreeting: agent.agentGreeting ?? null,
          knowledgeBases:
            agent.knowledgeBases?.map((kb: any) => ({
              id: kb.id,
              name: kb.name,
            })) ?? [],
        }));

        return {
          result: {
            count: personas.length,
            personas,
          },
        };
      } catch (err) {
        return {
          error: {
            message: err instanceof Error ? err.message : 'Failed to list personas',
          },
        };
      }
    },
  };
}

function createGetPersonaTool(
  client: GraphQLClient
): ClientTool {
  return {
    name: 'get_persona',
    description: `Get detailed information about a specific persona (agent) by its ID.
Returns the full configuration including name, system message, greeting,
model, tools configuration, knowledge bases, and enabled/public status.`,
    parameters: {
      type: 'object',
      properties: {
        id: {
          type: 'string',
          description: 'The ID of the persona to retrieve.',
        },
      },
      required: ['id'],
    },
    handler: async (args: { id: string }) => {
      if (!args.id) {
        return { error: { message: 'Persona ID is required.' } };
      }

      try {
        const { data } = await client.query({
          query: GetAgentDocument,
          variables: { id: args.id },
          fetchPolicy: 'network-only',
        });

        if (!data?.agent) {
          return { error: { message: `Persona with ID "${args.id}" not found.` } };
        }

        const agent = data.agent;
        return {
          result: {
            id: agent.id,
            name: agent.name,
            description: agent.description ?? null,
            isTemplate: agent.isTemplate ?? false,
            isPublic: agent.isPublic ?? false,
            isEnabled: agent.isEnabled ?? true,
            systemMessage: agent.systemMessage ?? null,
            agentGreeting: agent.agentGreeting ?? null,
            tools: agent.tools ?? null,
            context: agent.context ?? null,
            knowledgeBases:
              agent.knowledgeBases?.map((kb: any) => ({
                id: kb.id,
                name: kb.name,
                description: kb.description ?? null,
                status: kb.status ?? null,
                documentCount: kb.documentCount ?? 0,
              })) ?? [],
          },
        };
      } catch (err) {
        return {
          error: {
            message: err instanceof Error ? err.message : 'Failed to get persona',
          },
        };
      }
    },
  };
}

function createCreatePersonaTool(
  client: GraphQLClient
): ClientTool {
  return {
    name: 'create_persona',
    description: `Create a new persona (agent) with the given configuration.
At minimum, provide a name. You can also set the system message (instructions
for the LLM), greeting (initial message shown to users), model preference
(fast, smart, or premium), description, tools configuration, and collaborator
agents that this persona can consult via ask_agent.`,
    parameters: {
      type: 'object',
      properties: {
        name: {
          type: 'string',
          description: 'Display name for the persona (e.g. "Sales Assistant").',
        },
        systemMessage: {
          type: 'string',
          description:
            'System prompt / instructions that define the persona\'s behavior and personality.',
        },
        agentGreeting: {
          type: 'string',
          description:
            'Initial greeting message shown when a user starts a conversation with this persona.',
        },
        model: {
          type: 'string',
          enum: ['FAST', 'SMART', 'PREMIUM'],
          description:
            'LLM model tier to use. "FAST" is cheapest/fastest (Claude Haiku 4.5), "SMART" is balanced (Claude 3.7 Sonnet), "PREMIUM" is highest quality (Claude Sonnet 4.5).',
        },
        description: {
          type: 'string',
          description: 'Short description of what this persona does, shown in the persona list.',
        },
        tools: {
          type: 'object',
          description:
            'Tools configuration object. Call list_available_tools first to discover what tools are available, then list EVERY tool by name in tool_configs. Empty tool_configs means NO tools are assigned. Each tool_config entry needs enabled:true and consent_mode ("auto"=allow, "manual"=ask). The object must include all top-level keys: predefined_names, allow_patterns, deny_patterns, system_tools, mcp_servers, workflow_tools, connectors.',
        },
        collaboratorAgents: {
          type: 'array',
          description:
            'List of other personas this agent can consult via ask_agent. Use list_personas to find agent IDs first. Each entry needs the agent\'s id and name. The agent will be able to send messages to these collaborators during conversations.',
          items: {
            type: 'object',
            properties: {
              id: { type: 'string', description: 'The persona ID of the collaborator agent.' },
              name: { type: 'string', description: 'Display name of the collaborator agent.' },
              description: { type: 'string', description: 'Optional short description of what this collaborator does.' },
            },
            required: ['id', 'name'],
          },
        },
      },
      required: ['name'],
    },
    handler: async (args: {
      name: string;
      systemMessage?: string;
      agentGreeting?: string;
      model?: string;
      description?: string;
      tools?: Record<string, any>;
      collaboratorAgents?: Array<{ id: string; name: string; description?: string }>;
    }) => {
      if (!args.name) {
        return { error: { message: 'Persona name is required.' } };
      }

      try {
        const input: Record<string, any> = {
          name: args.name,
        };

        if (args.systemMessage !== undefined) input.systemMessage = args.systemMessage;
        if (args.agentGreeting !== undefined) input.agentGreeting = args.agentGreeting;
        if (args.model !== undefined) input.model = args.model;
        if (args.description !== undefined) input.description = args.description;

        // Build tools config, merging in collaborator agents if provided
        if (args.tools !== undefined || args.collaboratorAgents !== undefined) {
          const toolsConfig = normalizeToolsConfig(args.tools ?? {});
          if (args.collaboratorAgents?.length) {
            toolsConfig.collaborator_agents = buildCollaboratorAgentsConfig(args.collaboratorAgents);
          }
          input.tools = JSON.stringify(toolsConfig);
        }

        const { data } = await client.mutate({
          mutation: CreateAgentDocument,
          variables: { input },
          refetchQueries: [{ query: ListAllAgentsDocument }],
        });

        if (!data?.createAgent) {
          return { error: { message: 'Create persona mutation returned no data.' } };
        }

        return {
          result: {
            success: true,
            message: `Persona "${args.name}" created successfully.`,
            persona: {
              id: data.createAgent.id,
              model: data.createAgent.model,
              systemMessage: data.createAgent.systemMessage,
              agentGreeting: data.createAgent.agentGreeting,
              tools: data.createAgent.tools ?? null,
            },
          },
        };
      } catch (err) {
        return {
          error: {
            message: err instanceof Error ? err.message : 'Failed to create persona',
          },
        };
      }
    },
  };
}

function createUpdatePersonaTool(
  client: GraphQLClient
): ClientTool {
  return {
    name: 'update_persona',
    description: `Update an existing persona (agent) by its ID. Only the fields you
provide will be changed; omitted fields remain as-is. You can update the name,
system message, greeting, model, description, enabled status, tools configuration,
and collaborator agents.`,
    parameters: {
      type: 'object',
      properties: {
        id: {
          type: 'string',
          description: 'The ID of the persona to update.',
        },
        name: {
          type: 'string',
          description: 'New display name for the persona.',
        },
        systemMessage: {
          type: 'string',
          description: 'New system prompt / instructions.',
        },
        agentGreeting: {
          type: 'string',
          description: 'New initial greeting message.',
        },
        model: {
          type: 'string',
          enum: ['FAST', 'SMART', 'PREMIUM'],
          description: 'New LLM model tier. "FAST" (Claude Haiku 4.5), "SMART" (Claude 3.7 Sonnet), "PREMIUM" (Claude Sonnet 4.5).',
        },
        description: {
          type: 'string',
          description: 'New short description.',
        },
        isEnabled: {
          type: 'boolean',
          description: 'Enable or disable the persona.',
        },
        tools: {
          type: 'object',
          description:
            'Tools configuration object. Call list_available_tools first to discover what tools are available, then list EVERY tool by name in tool_configs. Empty tool_configs means NO tools are assigned. Each tool_config entry needs enabled:true and consent_mode ("auto"=allow, "manual"=ask). The object must include all top-level keys: predefined_names, allow_patterns, deny_patterns, system_tools, mcp_servers, workflow_tools, connectors.',
        },
        collaboratorAgents: {
          type: 'array',
          description:
            'List of other personas this agent can consult via ask_agent. Use list_personas to find agent IDs first. Each entry needs the agent\'s id and name. Replaces the existing collaborator list entirely.',
          items: {
            type: 'object',
            properties: {
              id: { type: 'string', description: 'The persona ID of the collaborator agent.' },
              name: { type: 'string', description: 'Display name of the collaborator agent.' },
              description: { type: 'string', description: 'Optional short description of what this collaborator does.' },
            },
            required: ['id', 'name'],
          },
        },
      },
      required: ['id'],
    },
    handler: async (args: {
      id: string;
      name?: string;
      systemMessage?: string;
      agentGreeting?: string;
      model?: string;
      description?: string;
      isEnabled?: boolean;
      tools?: Record<string, any>;
      collaboratorAgents?: Array<{ id: string; name: string; description?: string }>;
    }) => {
      if (!args.id) {
        return { error: { message: 'Persona ID is required.' } };
      }

      try {
        const input: Record<string, any> = { id: args.id };

        if (args.name !== undefined) input.name = args.name;
        if (args.systemMessage !== undefined) input.systemMessage = args.systemMessage;
        if (args.agentGreeting !== undefined) input.agentGreeting = args.agentGreeting;
        if (args.model !== undefined) input.model = args.model;
        if (args.description !== undefined) input.description = args.description;
        if (args.isEnabled !== undefined) input.isEnabled = args.isEnabled;

        // Build tools config, merging in collaborator agents if provided
        if (args.tools !== undefined || args.collaboratorAgents !== undefined) {
          // If only collaboratorAgents provided (no tools), fetch existing tools to preserve them
          let baseTools = args.tools ?? {};
          if (args.tools === undefined && args.collaboratorAgents !== undefined) {
            try {
              const { data: agentData } = await client.query({
                query: GetAgentDocument,
                variables: { id: args.id },
                fetchPolicy: 'network-only',
              });
              baseTools = agentData?.agent?.tools ?? {};
            } catch {
              // If fetch fails, start with empty config
            }
          }
          const toolsConfig = normalizeToolsConfig(baseTools);
          if (args.collaboratorAgents !== undefined) {
            toolsConfig.collaborator_agents = buildCollaboratorAgentsConfig(args.collaboratorAgents);
          }
          input.tools = JSON.stringify(toolsConfig);
        }

        const { data } = await client.mutate({
          mutation: UpdateAgentDocument,
          variables: { input },
          refetchQueries: [
            { query: ListAllAgentsDocument },
            { query: GetAgentDocument, variables: { id: args.id } },
          ],
        });

        if (!data?.updateAgent) {
          return { error: { message: 'Update persona mutation returned no data.' } };
        }

        return {
          result: {
            success: true,
            message: `Persona "${args.id}" updated successfully.`,
            persona: {
              id: data.updateAgent.id,
              model: data.updateAgent.model,
              systemMessage: data.updateAgent.systemMessage,
              agentGreeting: data.updateAgent.agentGreeting,
              tools: data.updateAgent.tools ?? null,
            },
          },
        };
      } catch (err) {
        return {
          error: {
            message: err instanceof Error ? err.message : 'Failed to update persona',
          },
        };
      }
    },
  };
}

function createListKnowledgeBasesTool(
  client: GraphQLClient
): ClientTool {
  return {
    name: 'list_knowledge_bases',
    description: `List all knowledge bases available in the current tenant.
Returns each knowledge base's id, name, description, status, and document
metadata. Use this to discover available knowledge bases before attaching
one to a persona.`,
    parameters: {
      type: 'object',
      properties: {},
    },
    handler: async () => {
      try {
        const { data } = await client.query({
          query: GetKnowledgeBasesDocument,
          fetchPolicy: 'network-only',
        });

        const knowledgeBases = (data?.knowledgeBases ?? []).map((kb: any) => ({
          id: kb.id,
          name: kb.name,
          description: kb.description ?? null,
          status: kb.status ?? null,
          documentCount: kb.documents?.length ?? 0,
        }));

        return {
          result: {
            count: knowledgeBases.length,
            knowledgeBases,
          },
        };
      } catch (err) {
        return {
          error: {
            message:
              err instanceof Error ? err.message : 'Failed to list knowledge bases',
          },
        };
      }
    },
  };
}

function createAttachKnowledgeBaseTool(
  client: GraphQLClient
): ClientTool {
  return {
    name: 'attach_knowledge_base',
    description: `Attach a knowledge base to a persona (agent) so it can use the
knowledge base's documents for retrieval-augmented generation (RAG).
You need both the persona ID and the knowledge base ID.`,
    parameters: {
      type: 'object',
      properties: {
        agentId: {
          type: 'string',
          description: 'The ID of the persona to attach the knowledge base to.',
        },
        knowledgeBaseId: {
          type: 'string',
          description: 'The ID of the knowledge base to attach.',
        },
      },
      required: ['agentId', 'knowledgeBaseId'],
    },
    handler: async (args: { agentId: string; knowledgeBaseId: string }) => {
      if (!args.agentId || !args.knowledgeBaseId) {
        return {
          error: { message: 'Both agentId and knowledgeBaseId are required.' },
        };
      }

      try {
        const { data } = await client.mutate({
          mutation: AttachKnowledgeBaseToAgentDocument,
          variables: {
            input: {
              agentId: args.agentId,
              knowledgeBaseId: args.knowledgeBaseId,
            },
          },
          refetchQueries: [
            { query: ListAllAgentsDocument },
            { query: GetAgentDocument, variables: { id: args.agentId } },
          ],
        });

        if (!data?.attachKnowledgeBaseToAgent) {
          return { error: { message: 'Attach knowledge base mutation returned no data.' } };
        }

        return {
          result: {
            success: true,
            message: `Knowledge base "${args.knowledgeBaseId}" attached to persona "${args.agentId}" successfully.`,
            attachment: {
              id: data.attachKnowledgeBaseToAgent.id,
              agentId: data.attachKnowledgeBaseToAgent.agentId,
              knowledgeBaseId: data.attachKnowledgeBaseToAgent.knowledgeBaseId,
            },
          },
        };
      } catch (err) {
        return {
          error: {
            message:
              err instanceof Error ? err.message : 'Failed to attach knowledge base',
          },
        };
      }
    },
  };
}

function createDetachKnowledgeBaseTool(
  client: GraphQLClient
): ClientTool {
  return {
    name: 'detach_knowledge_base',
    description: `Detach (remove) a knowledge base from a persona (agent). The knowledge
base itself is not deleted — it is only unlinked from the persona.`,
    parameters: {
      type: 'object',
      properties: {
        agentId: {
          type: 'string',
          description: 'The ID of the persona to detach the knowledge base from.',
        },
        knowledgeBaseId: {
          type: 'string',
          description: 'The ID of the knowledge base to detach.',
        },
      },
      required: ['agentId', 'knowledgeBaseId'],
    },
    handler: async (args: { agentId: string; knowledgeBaseId: string }) => {
      if (!args.agentId || !args.knowledgeBaseId) {
        return {
          error: { message: 'Both agentId and knowledgeBaseId are required.' },
        };
      }

      try {
        await client.mutate({
          mutation: DetachKnowledgeBaseFromAgentDocument,
          variables: {
            agentId: args.agentId,
            knowledgeBaseId: args.knowledgeBaseId,
          },
          refetchQueries: [
            { query: ListAllAgentsDocument },
            { query: GetAgentDocument, variables: { id: args.agentId } },
          ],
        });

        return {
          result: {
            success: true,
            message: `Knowledge base "${args.knowledgeBaseId}" detached from persona "${args.agentId}" successfully.`,
          },
        };
      } catch (err) {
        return {
          error: {
            message:
              err instanceof Error ? err.message : 'Failed to detach knowledge base',
          },
        };
      }
    },
  };
}
